import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTheme } from '../ThemeContext';
import { useToast } from '../components/Toast';
import { getAllScreenshots, updateScreenshot, BASE_URL } from '../api';

function formatFileSize(bytes) {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Group a flat array of screens by project_id, preserving project_tags from first screen
function groupByProject(screens) {
  const map = new Map();
  for (const s of screens) {
    if (!map.has(s.project_id)) {
      map.set(s.project_id, {
        project_id: s.project_id,
        project_title: s.project_title,
        project_tags: s.project_tags || [],
        screens: [],
      });
    }
    map.get(s.project_id).screens.push(s);
  }
  return Array.from(map.values());
}

export default function ScreenshotManager() {
  const { colors, common, mode } = useTheme();
  const toast = useToast();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState({});
  const [zoomedScreen, setZoomedScreen] = useState(null);
  const closeBtnRef = useRef(null);

  // ── Filters ──────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [activeTagIds, setActiveTagIds] = useState([]);
  const [showUnused, setShowUnused] = useState(false);

  useEffect(() => {
    getAllScreenshots()
      .then(({ data }) => {
        setGroups(groupByProject(data));
        const initial = {};
        data.forEach((s) => {
          initial[s.id] = { name: s.name || '', alt_text: s.alt_text || '' };
        });
        setEdits(initial);
      })
      .catch(() => toast('Could not load screenshots.', 'error'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (zoomedScreen) closeBtnRef.current?.focus();
  }, [zoomedScreen]);

  useEffect(() => {
    if (!zoomedScreen) return;
    const handler = (e) => { if (e.key === 'Escape') setZoomedScreen(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [zoomedScreen]);

  const handleChange = (id, field, value) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const handleBlur = useCallback(async (id) => {
    const edit = edits[id];
    if (!edit) return;
    setSaving((prev) => ({ ...prev, [id]: true }));
    try {
      await updateScreenshot(id, { name: edit.name, alt_text: edit.alt_text });
      toast('Saved.', 'success');
      setGroups((prev) =>
        prev.map((g) => ({
          ...g,
          screens: g.screens.map((s) =>
            s.id === id ? { ...s, name: edit.name, alt_text: edit.alt_text } : s
          ),
        }))
      );
    } catch {
      toast('Could not save changes. Please try again.', 'error');
    }
    setSaving((prev) => ({ ...prev, [id]: false }));
  }, [edits]);

  const toggleTag = (id) => {
    setActiveTagIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Collect all unique tags across every project
  const allTags = useMemo(() => {
    const seen = new Set();
    const tags = [];
    for (const g of groups) {
      for (const t of g.project_tags) {
        if (!seen.has(t.id)) {
          seen.add(t.id);
          tags.push(t);
        }
      }
    }
    return tags.sort((a, b) => a.name.localeCompare(b.name));
  }, [groups]);

  // Apply all active filters
  const filteredGroups = useMemo(() => {
    const tagSet = new Set(activeTagIds);
    return groups
      .filter((g) => {
        if (tagSet.size === 0) return true;
        return g.project_tags.some((t) => tagSet.has(t.id));
      })
      .map((g) => ({
        ...g,
        screens: g.screens.filter((s) => {
          if (showUnused && s.hotspot_count > 0) return false;
          if (!search) return true;
          const q = search.toLowerCase();
          const edit = edits[s.id] || { name: '', alt_text: '' };
          return (
            edit.name.toLowerCase().includes(q) ||
            edit.alt_text.toLowerCase().includes(q)
          );
        }),
      }))
      .filter((g) => g.screens.length > 0);
  }, [groups, edits, search, activeTagIds, showUnused]);

  const totalFiltered = filteredGroups.reduce((n, g) => n + g.screens.length, 0);
  const totalAll = groups.reduce((n, g) => n + g.screens.length, 0);
  const isFiltering = search || activeTagIds.length > 0 || showUnused;

  // ── Styles ──────────────────────────────────────────────────────────────────
  const s = {
    container: { ...common.page, minHeight: 'unset', flex: 1 },

    section: { marginBottom: 40 },
    sectionHeader: {
      display: 'flex', alignItems: 'center', gap: 12,
      marginBottom: 16, paddingBottom: 12,
      borderBottom: `2px solid ${colors.borderColor}`,
    },
    projectTitle: {
      margin: 0, fontSize: 17, fontWeight: 700, color: colors.textPrimary,
    },
    screenCount: {
      fontSize: 12, fontWeight: 600, color: colors.textMuted,
      backgroundColor: colors.hoverBg, border: `1px solid ${colors.borderColor}`,
      borderRadius: 20, padding: '2px 10px',
    },
    tagPill: {
      fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '1px 8px',
      border: '1px solid',
    },
    viewLink: {
      marginLeft: 'auto', fontSize: 13, color: colors.link,
      textDecoration: 'none', fontWeight: 600,
    },

    filterBar: {
      display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
      marginBottom: 24,
      padding: '12px 16px',
      backgroundColor: colors.cardBg,
      border: `1px solid ${colors.borderColor}`,
      borderRadius: 10,
    },
    searchInput: {
      ...common.input,
      marginBottom: 0, fontSize: 13, width: 220, flexShrink: 0,
    },
    filterLabel: {
      fontSize: 12, fontWeight: 600, color: colors.textMuted,
      textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0,
    },
    tagToggle: (active, color) => ({
      fontSize: 12, fontWeight: 600, borderRadius: 20,
      padding: '3px 12px', cursor: 'pointer', border: 'none',
      backgroundColor: active ? color : colors.hoverBg,
      color: active ? '#fff' : colors.textSecondary,
      outline: 'none',
    }),
    unusedToggle: (active) => ({
      fontSize: 12, fontWeight: 600, borderRadius: 20,
      padding: '3px 12px', cursor: 'pointer',
      border: `1px solid ${active ? colors.amber : colors.borderColor}`,
      backgroundColor: active ? colors.amber : colors.hoverBg,
      color: active ? '#fff' : colors.textSecondary,
    }),
    filterSummary: {
      fontSize: 13, color: colors.textMuted, marginBottom: 16,
    },
    clearBtn: {
      marginLeft: 4, fontSize: 12, color: colors.indigo,
      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
      textDecoration: 'underline', fontFamily: 'inherit',
    },

    row: {
      display: 'grid',
      gridTemplateColumns: '140px 1fr 1fr',
      gap: 16,
      alignItems: 'center',
      padding: '14px 20px',
      borderRadius: 10,
      border: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.cardBg,
      marginBottom: 10,
    },
    thumbBtn: {
      position: 'relative', padding: 0, border: 'none', background: 'none',
      cursor: 'zoom-in', borderRadius: 6, display: 'block', flexShrink: 0,
      width: 140, height: 80,
    },
    thumb: {
      width: 140, height: 80, objectFit: 'cover',
      borderRadius: 6, display: 'block',
      border: `1px solid ${colors.borderColor}`,
    },
    thumbOverlay: {
      position: 'absolute', inset: 0, borderRadius: 6,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0)', fontSize: 22,
      transition: 'background-color 0.15s',
    },
    thumbCell: {
      display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start',
    },
    imageStats: {
      fontSize: 11, color: colors.textMuted, lineHeight: 1.4, whiteSpace: 'nowrap',
    },
    unusedBadge: {
      fontSize: 10, fontWeight: 700, color: colors.amber,
      border: `1px solid ${colors.amber}`, borderRadius: 4,
      padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '0.04em',
    },

    lightboxOverlay: {
      position: 'fixed', inset: 0, zIndex: 500,
      backgroundColor: 'rgba(0,0,0,0.88)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    },
    lightboxInner: {
      position: 'relative', display: 'flex', flexDirection: 'column',
      alignItems: 'center', maxWidth: '90vw',
      animation: 'lightbox-pop 0.18s ease forwards',
    },
    lightboxClose: {
      position: 'absolute', top: -16, right: -16, zIndex: 10,
      width: 36, height: 36, borderRadius: '50%',
      backgroundColor: 'rgba(255,255,255,0.15)',
      border: '1px solid rgba(255,255,255,0.25)',
      color: '#fff', fontSize: 16, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'inherit',
    },
    lightboxImg: {
      maxWidth: '90vw', maxHeight: '80vh',
      borderRadius: 8, display: 'block',
      boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
    },
    lightboxCaption: { marginTop: 16, textAlign: 'center', maxWidth: 640 },

    fieldWrap: { display: 'flex', flexDirection: 'column', gap: 4 },
    fieldLabel: {
      fontSize: 11, fontWeight: 600, color: colors.textMuted,
      textTransform: 'uppercase', letterSpacing: '0.05em',
    },
    input: { ...common.input, marginBottom: 0, fontSize: 13, borderColor: colors.borderColor },
    savingDot: {
      display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
      backgroundColor: colors.indigo, marginLeft: 6,
      animation: 'pulse 1s ease-in-out infinite',
    },
    empty: { ...common.empty, marginTop: 40 },
  };

  return (
    <div style={s.container}>
      <header style={common.header}>
        <div style={common.headerLeft}>
          <a href="/dashboard" aria-label="Back to dashboard" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <img
              src={mode === 'dark' ? '/Logo-Dark.png' : '/Logo-Light.png'}
              alt="Software Simulation Builder logo"
              style={{ height: 32, width: 'auto' }}
            />
          </a>
          <a href="/dashboard" style={{ ...common.btn, ...common.btnGhost, textDecoration: 'none' }}>
            ← Back
          </a>
          <h1 style={common.appTitle}>Screenshot Manager</h1>
        </div>
      </header>

      <main id="main-content" style={common.content}>
        <div style={{ ...common.topBar, marginBottom: 8 }}>
          <div>
            <h2 style={common.pageHeading}>All Screenshots</h2>
            <p style={{ color: colors.textSecondary, fontSize: 14, marginTop: 4 }}>
              Add names and alt text to your screenshots. Changes save automatically when you leave a field.
            </p>
          </div>
        </div>

        {/* Alt text guidance */}
        <div style={{
          backgroundColor: colors.cardBg, border: `1px solid ${colors.borderColor}`,
          borderLeft: `4px solid ${colors.indigo}`,
          borderRadius: 10, padding: '14px 20px', marginBottom: 24,
          display: 'flex', gap: 16, alignItems: 'flex-start',
        }}>
          <div>
            <p style={{ margin: '0 0 4px', color: colors.textPrimary, fontWeight: 600, fontSize: 14 }}>
              Tips for good alt text and names
            </p>
            <ul style={{ margin: 0, paddingLeft: 20, color: colors.textSecondary, fontSize: 13, lineHeight: 1.7 }}>
              <li><strong style={{ color: colors.textPrimary }}>Name</strong> — a short label for the screenshot (e.g. "Layer panel open"). Used as the step label in exported simulations.</li>
              <li><strong style={{ color: colors.textPrimary }}>Alt text</strong> — a description for screen readers (e.g. "Photoshop window showing the Layers panel with three adjustment layers"). Aim for 1–2 sentences.</li>
            </ul>
          </div>
        </div>

        {/* ── Filter bar ── */}
        {!loading && groups.length > 0 && (
          <div style={s.filterBar} role="search" aria-label="Filter screenshots">
            <input
              style={s.searchInput}
              type="search"
              placeholder="Search by name or alt text…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search screenshots"
            />

            {allTags.length > 0 && (
              <>
                <span style={s.filterLabel}>Tag</span>
                {allTags.map((t) => {
                  const active = activeTagIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      style={s.tagToggle(active, t.color)}
                      onClick={() => toggleTag(t.id)}
                      aria-pressed={active}
                    >
                      {t.name}
                    </button>
                  );
                })}
              </>
            )}

            <button
              style={s.unusedToggle(showUnused)}
              onClick={() => setShowUnused((v) => !v)}
              aria-pressed={showUnused}
            >
              No hotspots only
            </button>

            {isFiltering && (
              <button
                style={s.clearBtn}
                onClick={() => { setSearch(''); setActiveTagIds([]); setShowUnused(false); }}
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Filter summary */}
        {isFiltering && !loading && (
          <p style={s.filterSummary}>
            Showing {totalFiltered} of {totalAll} screenshot{totalAll !== 1 ? 's' : ''}
          </p>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: colors.textMuted, fontSize: 15 }}>
            Loading screenshots…
          </div>
        ) : groups.length === 0 ? (
          <div style={s.empty}>
            <p>No screenshots yet. Upload some screens to a simulation to get started.</p>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div style={s.empty}>
            <p>No screenshots match the current filters.</p>
          </div>
        ) : (
          filteredGroups.map((group) => (
            <div key={group.project_id} style={s.section}>
              <div style={s.sectionHeader}>
                <h2 style={s.projectTitle}>{group.project_title}</h2>
                <span style={s.screenCount}>
                  {group.screens.length} screen{group.screens.length !== 1 ? 's' : ''}
                </span>
                {group.project_tags.map((t) => (
                  <span
                    key={t.id}
                    style={{ ...s.tagPill, color: t.color, borderColor: t.color, backgroundColor: `${t.color}18` }}
                  >
                    {t.name}
                  </span>
                ))}
                <a
                  href={`/projects/${group.project_id}`}
                  style={s.viewLink}
                  aria-label={`Open ${group.project_title}`}
                >
                  Open simulation ↗
                </a>
              </div>

              {group.screens.map((screen, idx) => {
                const edit = edits[screen.id] || { name: '', alt_text: '' };
                const isSaving = saving[screen.id];
                const isUnused = screen.hotspot_count === 0;
                return (
                  <div key={screen.id} style={s.row} aria-label={`Screen ${idx + 1} of ${group.project_title}`}>
                    {/* Thumbnail + stats */}
                    <div style={s.thumbCell}>
                      <button
                        style={s.thumbBtn}
                        onClick={() => setZoomedScreen({ ...screen, idx })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setZoomedScreen({ ...screen, idx });
                          }
                        }}
                        aria-label={`Zoom in: ${edit.name || `Screen ${idx + 1}`}`}
                        title="Click to zoom in"
                      >
                        <img
                          src={`${BASE_URL}/uploads/${screen.image_path}`}
                          alt=""
                          aria-hidden="true"
                          style={s.thumb}
                        />
                        <span style={s.thumbOverlay} aria-hidden="true">🔍</span>
                      </button>
                      <p style={s.imageStats}>
                        {[
                          screen.width && screen.height ? `${screen.width} × ${screen.height}` : null,
                          screen.format || null,
                          formatFileSize(screen.file_size),
                        ].filter(Boolean).join(' · ')}
                      </p>
                      {isUnused && (
                        <span style={s.unusedBadge} title="This screen has no hotspots">No hotspots</span>
                      )}
                    </div>

                    {/* Name field */}
                    <div style={s.fieldWrap}>
                      <label htmlFor={`name-${screen.id}`} style={s.fieldLabel}>
                        Name {isSaving && <span style={s.savingDot} aria-hidden="true" />}
                      </label>
                      <input
                        id={`name-${screen.id}`}
                        style={s.input}
                        type="text"
                        value={edit.name}
                        placeholder={`Screen ${idx + 1}`}
                        onChange={(e) => handleChange(screen.id, 'name', e.target.value)}
                        onBlur={() => handleBlur(screen.id)}
                      />
                    </div>

                    {/* Alt text field */}
                    <div style={s.fieldWrap}>
                      <label htmlFor={`alt-${screen.id}`} style={s.fieldLabel}>
                        Alt Text
                      </label>
                      <input
                        id={`alt-${screen.id}`}
                        style={s.input}
                        type="text"
                        value={edit.alt_text}
                        placeholder="Describe what is shown in this screenshot"
                        onChange={(e) => handleChange(screen.id, 'alt_text', e.target.value)}
                        onBlur={() => handleBlur(screen.id)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </main>

      {/* ── Lightbox ── */}
      {zoomedScreen && (
        <div
          style={s.lightboxOverlay}
          role="dialog"
          aria-modal="true"
          aria-label={`Zoomed: ${edits[zoomedScreen.id]?.name || `Screen ${zoomedScreen.idx + 1}`}`}
          onClick={(e) => { if (e.target === e.currentTarget) setZoomedScreen(null); }}
        >
          <div style={s.lightboxInner}>
            <button
              ref={closeBtnRef}
              style={s.lightboxClose}
              onClick={() => setZoomedScreen(null)}
              aria-label="Close zoom"
            >
              ✕
            </button>
            <img
              src={`${BASE_URL}/uploads/${zoomedScreen.image_path}`}
              alt={edits[zoomedScreen.id]?.alt_text || `Screen ${zoomedScreen.idx + 1}`}
              style={s.lightboxImg}
            />
            {(edits[zoomedScreen.id]?.name || edits[zoomedScreen.id]?.alt_text) && (
              <div style={s.lightboxCaption}>
                {edits[zoomedScreen.id]?.name && (
                  <p style={{ margin: 0, fontWeight: 700, color: '#f9fafb', fontSize: 15 }}>
                    {edits[zoomedScreen.id].name}
                  </p>
                )}
                {edits[zoomedScreen.id]?.alt_text && (
                  <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: 13, lineHeight: 1.5 }}>
                    {edits[zoomedScreen.id].alt_text}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
